import { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, Select, Skeleton, Tag, Typography } from 'antd';
import { BlockOutlined } from '@ant-design/icons';
import { listTemplates } from '@/api/templates';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { TemplateSummary } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { usePageTitle } from '@/components/pageTitle';
import { TemplateCard } from '@/templates/TemplateCard';
import { TemplateDrawer } from '@/templates/TemplateDrawer';

const { Text } = Typography;
const { CheckableTag } = Tag;

const CATEGORIES = [
  { value: 'CURATED', label: 'Curated' },
  { value: 'ESTATE_CANDIDATE', label: 'Estate candidates' },
];

export function TemplatesPage() {
  usePageTitle('Templates');

  const [category, setCategory] = useState<string | undefined>();
  const [jurisdiction, setJurisdiction] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    setError(null);
    try {
      const res = await listTemplates({ category, jurisdiction });
      setTemplates(res.templates);
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else setError(n.message);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [category, jurisdiction]);

  useEffect(() => {
    void load();
  }, [load]);

  // Jurisdiction options derive from the loaded set (server also filters server-side via the param).
  const jurisdictionOptions = useMemo(() => {
    const set = new Set<string>();
    templates.forEach((t) => t.jurisdiction && set.add(t.jurisdiction));
    return [...set].sort().map((j) => ({ value: j, label: j }));
  }, [templates]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q) ||
        (t.jurisdiction ?? '').toLowerCase().includes(q),
    );
  }, [templates, search]);

  return (
    <div className="ol-page">
      <PageHeader
        title="Template gallery"
        icon={<BlockOutlined />}
        subtitle="Start from a ready-made flow. Curated templates are authored and maintained by the platform team, with their regulatory notes; estate candidates are harvested from the demo estate and need review. Pick one, preview it, and instantiate it as a new draft in the designer."
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 18 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>
            Category
          </Text>
          <CheckableTag checked={category === undefined} onChange={() => setCategory(undefined)}>
            All
          </CheckableTag>
          {CATEGORIES.map((c) => (
            <CheckableTag
              key={c.value}
              checked={category === c.value}
              onChange={(on) => setCategory(on ? c.value : undefined)}
            >
              {c.label}
            </CheckableTag>
          ))}
        </div>
        <Select
          allowClear
          showSearch
          placeholder="Jurisdiction"
          style={{ width: 180 }}
          value={jurisdiction}
          onChange={setJurisdiction}
          options={jurisdictionOptions}
          optionFilterProp="label"
        />
        <Input.Search
          placeholder="Search templates"
          allowClear
          style={{ width: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="ol-card">
              <Skeleton active paragraph={{ rows: 3 }} />
            </div>
          ))}
        </div>
      ) : error ? (
        <ErrorState what="the template gallery" detail={error} onRetry={() => void load()} />
      ) : unavailable ? (
        <EmptyState
          icon={<BlockOutlined />}
          title="Template gallery"
          description="Reusable flow templates land here once the template API is live (Track A, PS-4). Curated templates are authored by the platform team; the 92 demo-estate candidates (ADR-012 #10) surface here as review candidates."
          bullets={[
            'Curated templates by jurisdiction and category',
            'Estate candidates harvested from the migration corpus',
            'A flow preview before you commit',
            'Instantiate-as-variant → a new draft in the designer',
          ]}
        />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<BlockOutlined />}
          title="No templates match"
          description="No templates match the current filters. Clear a filter, or broaden your search."
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {visible.map((t) => (
            <TemplateCard key={t.templateId} template={t} onOpen={() => setOpenId(t.templateId)} />
          ))}
        </div>
      )}

      <TemplateDrawer templateId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
